// Copyright ©️ 2026 eVolve MEP, LLC

import './work-order-detail.css';

import React, { useMemo, useEffect, useState, useActionState, type ReactNode } from 'react';

import { type WorkOrder, useEvolveGet, type WorkOrderId, type RollupEntityAttachmentType } from '@evolvemep/api-ts';
import { LoadingOverlay, Stack, Flex, Loader } from '@mantine/core';
import { useParams } from 'react-router';

import { AttachmentList } from 'components/Mantine/Attachments/AttachmentList';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { DescriptionEditor } from 'components/Mantine/DetailPage/DescriptionEditor';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNil } from 'helpers/isNotNil';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { MachineElementActionsProvider } from 'modules/Shop/EvolveFabrication/machineElementActions/useMachineElementActions';
import { WorkOrderItemsList } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemsList';
import { WorkOrderDetailModalProvider } from 'modules/Shop/WorkOrders/WorkOrderDetail/modalRegistries';
import { useWorkOrderDetailMutations } from 'modules/Shop/WorkOrders/WorkOrderDetail/useWorkOrderDetailMutations';
import { WorkOrderActivityFeed } from 'modules/Shop/WorkOrders/WorkOrderDetail/WorkOrderActivityFeed';
import { WorkOrderDetailSidePane } from 'modules/Shop/WorkOrders/WorkOrderDetail/WorkOrderDetailSidePane';
import { WorkOrderActionButtons } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderActionButtons';

export type WorkOrderDetailPageProps = {
  workOrder: WorkOrder;
  onWorkOrderUpdated: (workOrder: WorkOrder) => void;
};

export const WorkOrderDetailPageWrapped = ({ workOrder, onWorkOrderUpdated }: WorkOrderDetailPageProps) => {
  const { updateWorkOrder, refreshWorkOrder, editable, saving, loading, canEditWorkOrder } =
    useWorkOrderDetailMutations(workOrder, onWorkOrderUpdated);

  const [attachmentRefreshTriggers, onRollupAttachmentsChanged] = useActionState<
    Partial<Record<RollupEntityAttachmentType, number>>,
    RollupEntityAttachmentType
  >((triggers, section) => ({ ...triggers, [section]: (triggers[section] ?? 0) + 1 }), {});

  return (
    <MachineElementActionsProvider route="/work-orders/:workOrderId">
      <WorkOrderDetailModalProvider>
        <Stack flex={1} style={{ overflow: 'hidden' }} gap={0}>
          <BasePageHeader
            title={workOrder.identifier}
            parentPage={{
              title: 'Work Orders',
              url: '/work-orders',
            }}
            topRightComponent={
              <>
                {loading && <Loader size="sm" />}
                <WorkOrderActionButtons workOrder={workOrder} onWorkOrderUpdated={onWorkOrderUpdated} inline={false} />
              </>
            }
          />
          <Flex gap="md" flex="1 0 0" pr={8} align="flex-start" style={{ overflowY: 'scroll' }}>
            {/* Large left column */}
            <Stack flex={1}>
              <DescriptionEditor
                title={workOrder.name}
                description={workOrder.description}
                updateDescription={(description) => updateWorkOrder({ description })}
                editable={editable}
              />

              <WorkOrderItemsList
                workOrder={workOrder}
                forceWorkOrderRefresh={refreshWorkOrder}
                onItemAttachmentsChanged={() => onRollupAttachmentsChanged('workOrderItems')}
                onTaskAttachmentsChanged={() => onRollupAttachmentsChanged('workOrderBop')}
              />

              <DetailSection title="Attachments" large>
                <AttachmentList
                  workOrderId={workOrder.workOrderId}
                  allowUploads="workOrder"
                  refreshTriggers={attachmentRefreshTriggers}
                />
              </DetailSection>

              <DetailSection title="Activity" large>
                <WorkOrderActivityFeed workOrder={workOrder} />
              </DetailSection>
            </Stack>

            {/* Thin right column */}
            <Stack miw={300} w={300}>
              <WorkOrderDetailSidePane
                workOrder={workOrder}
                updateWorkOrder={updateWorkOrder}
                refreshWorkOrder={refreshWorkOrder}
                editable={editable}
                saving={saving}
                loading={loading}
                canEditWorkOrder={canEditWorkOrder}
              />
            </Stack>
          </Flex>
        </Stack>
      </WorkOrderDetailModalProvider>
    </MachineElementActionsProvider>
  );
};

/**
 * Used to verify the selected `WorkOrder` both exists and is loaded into memory.
 * Doubles as the mobile route `Layout` for the Work Order detail sub-pages.
 */
export const WorkOrderDetailWrapper = ({
  children,
}: {
  children: (context: WorkOrderDetailPageProps) => ReactNode;
}) => {
  const navigate = useEvolveNavigate('', true);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);

  const { workOrderId } = useParams() as { workOrderId?: WorkOrderId };
  const { apiCall: getWorkOrder, loading } = useEvolveGet(`shop/workOrder/:workOrderId`);
  const { uoms } = useUnitOfMeasure();
  const workOrderMatches = useMemo(
    () => selectedWorkOrder?.workOrderId === workOrderId,
    [selectedWorkOrder?.workOrderId, workOrderId],
  );
  useEffect(() => {
    if (isNil(workOrderId)) {
      void navigate(`/work-orders`);
    } else if (!workOrderMatches) {
      getWorkOrder({ url: `shop/workOrder/${workOrderId}` })
        .then(setSelectedWorkOrder)
        .catch(() => {
          void navigate(`/work-orders`);
        });
    }
  }, [getWorkOrder, navigate, setSelectedWorkOrder, workOrderMatches, workOrderId]);

  if (isNil(selectedWorkOrder) || isNil(uoms) || loading || !workOrderMatches) {
    return <LoadingOverlay visible />;
  }
  return children({ workOrder: selectedWorkOrder, onWorkOrderUpdated: setSelectedWorkOrder });
};

export const WorkOrderDetailPage = () => (
  <WorkOrderDetailWrapper>{(context) => <WorkOrderDetailPageWrapped {...context} />}</WorkOrderDetailWrapper>
);
